<script lang="ts" setup>
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { defineAsyncComponent } from '@@/bits/vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGroupPickerStore } from '@@/pinia/surface_group_picker'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { AttachmentProps } from '@@/types'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { useSurfaceAddPostButton } from '@@/vuecomposables/surface_add_post_button'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const ContentPicker = defineAsyncComponent(() => import('@@/vuecomponents/ContentPicker.vue'))
const SurfaceAddPostButtonOld = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButtonOld.vue'))
const SurfaceGroupPicker = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGroupPicker.vue'))

const { contentSources } = usePostContentPickerSources()

const contentPickerStore = useContentPickerStore()
const surfaceStore = useSurfaceStore()
const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceDraftsStore = useSurfaceDraftsStore()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfacePostsStore = useSurfacePostsStore()
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const windowSizeStore = useWindowSizeStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const darkModeStore = useDarkModeStore()
const { isDarkMode } = storeToRefs(darkModeStore)

const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

const surfaceGroupPickerStore = useSurfaceGroupPickerStore()
const { isVisible: xGroupPicker, title: groupPickerTitle } = storeToRefs(surfaceGroupPickerStore)

const { showOverFileSizeModal } = surfaceStore
const { uploadFileActiveDraft, updateActiveDraft } = surfaceDraftsStore
const { hidePostContentPicker } = surfaceContentPickerStore
const { windowHeight } = storeToRefs(windowSizeStore)

const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)
const { handleAddPostButtonClick, xAddPostButton } = useSurfaceAddPostButton()

/**
 * Opens the wall in a new window for LTI (Learning Tools Interoperability) handling.
 * This is used when a user needs to authenticate or access the wall through an LTI integration.
 * The URL opened is the login and redirect URL stored in the surface store.
 */
const openInNewWindow = (): void => {
  window.open(surfaceStore.loginAndRedirectToWallUrl, '_blank')
}

const onUploadStart = ({ url }: { url?: string }) => {
  if (url) {
    surfacePostsStore.saveContent({ content: { link: url } })
  }
}

const onUploadDone = (url: string, attachmentProps?: AttachmentProps) => {
  contentPickerStore.hideContentPickerPanel()
  const shouldCreateNewPost = ![ContentPickerSource.GoogleDrive, ContentPickerSource.MicrosoftOneDrive].includes(
    attachmentProps?.contentSource,
  )
  surfacePostsStore.saveContent({
    content: { link: url, altText: attachmentProps?.altText, shouldCreateNewPost },
  })
  if (surfaceDraftsStore.activeDraft?.shouldAutoPublish) {
    surfacePostsStore.savePost({ attributes: surfaceDraftsStore.activeDraft })
  }
}

const onUploadReject = (file: File) => {
  showOverFileSizeModal({ file })
}

const onUploadError = () => {
  surfaceDraftsStore.stopUploadFileActiveDraft()
  if (surfaceDraftsStore.activeDraft) {
    surfaceDraftsStore.stopAutoPublishDraft(surfaceDraftsStore.activeDraft.cid)
  }
}

const onUploadProgress = (progress: number) => {
  surfaceDraftsStore.updateActiveDraft({ uploadProgress: progress })
}

const showExtensionRequired = (): void => {
  surfaceRequireNoticeStore.showExtensionRequired()
  surfaceRequireNoticeStore.addAfterHideExtensionRequiredAction(() => {
    contentPickerStore.hideContentPickerPanel()
    hidePostContentPicker()
  })
}

const setCaption = (caption: string) => {
  if (!caption) return

  surfacePostsStore.saveAttachmentCaption({ caption })
}

const xContentPicker = computed(() => {
  return (
    (contentPickerStore.xContentSourceMenu === true || contentPickerStore.activeContentPickerPanel !== null) &&
    surfaceContentPickerStore.xPostContentPicker
  )
})

const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
        ContentPickerSource.AiImageSearch,
      ].includes(source.type as ContentPickerSource),
    )
})

const googleDriveUploadKey = computed(() => {
  return surfaceDraftsStore.activeDraft ? `draft:${surfaceDraftsStore.activeDraft.cid}` : undefined
})
</script>

<template>
  <div :dir="dir()" class="z-addpost" @click.stop="recordWhitelistedClick" @keydown.stop="() => {}">
    <Teleport v-if="xContentPicker" to="#content-picker-portal-target">
      <ContentPicker
        :enable-hidden-inputs="device.mobile"
        :preferred-content-source-menu-start-height="500"
        :content-source-menu-list="contentSources"
        :x-search-bar="xSearchBar"
        :is-online="surfaceStore.isOnline"
        :is-lti-embedded="surfaceStore.isLtiEmbedded"
        :is-microsoft-teams-embedded="surfaceStore.isMicrosoftTeamsEmbedded"
        :should-upload-file="!surfaceDraftsStore.isAnyDraftActive"
        :active-draft="surfaceDraftsStore.activeDraft"
        :search-modes="surfaceContentPickerStore.currentSearchModes"
        :default-search-mode="surfaceContentPickerStore.defaultSearchMode"
        :search-suggestions="surfaceContentPickerStore.searchSuggestions"
        :google-drive-upload-key="googleDriveUploadKey"
        :user="surfaceStore.user"
        :wall="surfaceStore.wallAttributes"
        :wall-post-properties="surfacePostPropertiesStore.wallPostProperties"
        :teleport-target="'#surface-container'"
        @open-in-new-window="openInNewWindow"
        @close-content-picker="hidePostContentPicker"
        @upload-start="onUploadStart"
        @upload-done="onUploadDone"
        @upload-reject="onUploadReject"
        @upload-error="onUploadError"
        @upload-progress="onUploadProgress"
        @select-link="onUploadDone"
        @update-active-draft="updateActiveDraft"
        @select-file="uploadFileActiveDraft"
        @show-extension-required="showExtensionRequired"
        @set-caption="setCaption"
      />
    </Teleport>
    <SurfaceAddPostButtonOld v-if="!isAppUsing('surfaceTopbar') && xAddPostButton" @click="handleAddPostButtonClick" />
    <SurfaceGroupPicker
      v-if="xGroupPicker"
      :groups="surfaceWishArrangementStore.sortedGroups"
      :excluded-group-ids="surfaceGroupPickerStore.excludedGroupIds"
      :dark-mode="isDarkMode"
      :window-height="windowHeight"
      :full-size="isContainerSmallerThanTabletPortrait"
      :can-create-new-group="surfacePermissionsStore.canCreateNewGroup"
      :post-group-by="surfaceWishArrangementStore.wishGroupBy"
      :create-section="surfaceSectionsStore.createSectionWithName"
      :title="groupPickerTitle"
    />
  </div>
</template>
